<template>
  <div
    v-if="detailData"
    class="workpermit-detail"
  >
    <ProjectInfo
      v-if="notStandalone"
      :projectPermit="projectPermit"
    />
    <template>
      <Collapse
        :id="detailData.id"
        class="workpermit-detail__collapse"
        :expand="hoistingsExpandMap[detailData.id]"
        @expand-status="updateExpandMap"
      >
        <CollapseTitleSlot
          :id="detailData.id"
          slot="title"
          :key="detailData.id"
          :showDot="showDotOption(detailData)"
          :permitData="detailData"
          :dotOption="dotOption"
        />
        <WPCollapseContent
          slot="content"
          :permitData="detailData"
          :isProjectAvailable="isProjectAvailable"
          class="workpermit-detail__content"
        />
      </Collapse>
      <Collapse
        v-for="item in hoistings"
        v-if="!(item.status === WORK_PERMIT_STATUS.DRAFT && isEngineer)"
        :id="item.id"
        :key="item.id"
        class="workpermit-detail__collapse"
        :expand="hoistingsExpandMap[item.id] || !item.createdAt"
        @expand-status="updateExpandMap"
      >
        <CollapseTitleSlot
          :id="item.id"
          slot="title"
          :key="item.id"
          :showDot="showDotOptionForHoisting(item)"
          :permitData="item"
          :dotOption="dotOptionForHoisting(item)"
        />
        <HoistingCollapseContent
          slot="content"
          class="workpermit-detail__content"
          :permitData="item"
          :floorsOptions="floorsOptions"
          :isEdit="!item.createdAt"
          :isProjectAvailable="isProjectAvailable"
          :isPPSuspened="isPPSuspened"
          :selected="focusPermitId === item.id"
          @expand-status="updateExpandMap"
        />
      </Collapse>
      <div v-if="showHoistingModule && showAddHoistingBtn">
        <div
          class="addNewHoisting"
          @click="_addHoisting"
        >
          {{ addHoistingLabel }}
        </div>
        <div class="workPermit_hint">
          {{ $t(`termsAndConditions.${regionName}.workPermit.hintForEdinburghTower`, [hoistingLabel, 5]) }}
        </div>
        <div class="workPermit_hint">
          {{ $t(`termsAndConditions.${regionName}.workPermit.hintForOthers`, [hoistingLabel, 3]) }}
        </div>
        <div class="workPermit_hint">
          {{ $t(`termsAndConditions.${regionName}.workPermit.hintForPermitNumberLimit`, [ maxWAPPRHoistingNum, standalone ? $t('workPermit.HoistingLimitHintType.work') : $t('workPermit.HoistingLimitHintType.project'), hoistingLabel ]) }}
        </div>
      </div>
    </template>
    <ActionConfirmModal
      ref="deleteHoistingModal"
      :modalId="`delete-hoisting-${+new Date()}`"
      :confirmTitle="hoistingDeleteConfirmHint"
    />
    <ActionConfirmModal
      ref="noShowHoistingModal"
      :modalId="`noShow-hoisting-${+new Date()}`"
      :confirmTitle="hoistingNoShowHint"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Collapse from '@/controls/Collapse'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import EventBus from '@/utils/eventBus'
import mutationTypes from '@/store/mutation-types'
import { allowCreate } from '@/api/workPermit/util'
import { getMaxWAPPRHoistingNum } from '@/api/workPermit/request'
import { standaloneWpRouteNames } from '@/utils/consts'
import { tmsPermsTypes } from '@/utils/aclHelper'
import { orderByPermitCode } from '@/utils/sortingHelper'
import CollapseTitleSlot from './CollapseTitleSlot'
import HoistingCollapseContent from './HoistingCollapseContent'
import WPCollapseContent from './WPCollapseContent'
import ProjectInfo from './ProjectInfo'
import { WORK_PERMIT_STATUS, WORK_PERMIT_TYPES, DEFAULT_MAX_WAPPR_HOISTING_NUM } from './consts'
import { getAddHoistingLabel, getHoistingConfirmHint, getHoistingNoShowHint, getRegionalWorkPermitLabel } from './regionalWorkPermitLabels'
import {
  canProcessCurrentWP,
  composeNewHoistingPayload,
  disableTenantContractorEdit,
  isStandaloneWpContractor,
  isStandaloneWpTenant,
  isStandaloneWpTenancyContractor,
  isWorkPermitEditable,
} from './utils'

export default {
  name: 'WorkPermitDetail',
  components: {
    Collapse,
    CollapseTitleSlot,
    WPCollapseContent,
    HoistingCollapseContent,
    ProjectInfo,
    ActionConfirmModal,
  },
  props: {
    focusPermitId: String,
    isPPSuspened: {
      type: Boolean,
      default: false,
    },
    projectPermit: {
      type: [String, Object],
      default: '',
    },
    isProjectAvailable: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      chooseDeleteHoisting: '',
      chooseHoistingToExpired: '',
      hoistingsExpandMap: {},
      maxWAPPRHoistingNum: DEFAULT_MAX_WAPPR_HOISTING_NUM,
      WORK_PERMIT_STATUS,
    }
  },
  computed: {
    ...mapGetters('workPermit', ['currentWorkPermit', 'buildingFloorZoneMap', 'workPermitsInProject']),
    ...mapGetters('tenantProject', ['currentTenantProject']),
    ...mapGetters('projects', ['currentProject']),
    ...mapGetters('projectContractors', ['myContractor', 'isCurrentVendorAccount']),
    ...mapGetters('user', ['currentUser', 'isTenant', 'isEngineer']),
    ...mapGetters('regions', ['regionName', 'backEndRegionalEnv']),
    isStandalonePage () {
      return Object.values(standaloneWpRouteNames).includes(this.$route.name)
    },
    dotOption () {
      return [{
        title: this.$t('action.delete'),
        operate: this.openDeleteHoistingModal,
      }]
    },
    dotOptionForNoShow () {
      return [{
        title: this.$t('workPermit.noshow'),
        operate: this.openNoShowModal,
      }]
    },
    detailData () {
      return this.currentWorkPermit
    },
    notStandalone () {
      return this.currentWorkPermit && !this.currentWorkPermit.standalone
    },
    standalone () {
      return this.currentWorkPermit && this.currentWorkPermit.standalone
    },
    isStandaloneContractor () {
      return isStandaloneWpContractor(this.currentWorkPermit)
    },
    isStandaloneTenant () {
      return isStandaloneWpTenant(this.currentWorkPermit, this.currentUser)
    },
    isStandaloneTenancyContractor () {
      return isStandaloneWpTenancyContractor(this.currentWorkPermit, this.currentUser)
    },
    hoistings () {
      let result = lodash.get(this, 'currentWorkPermit.hoistings', [])
      // Because the get project's WPs API always returns all WPs(It means that all hoistings are returned)
      // So I computes the hoistings in client side in order to reduce server side calculations and API data size.
      if (!result.length && !this.isStandalonePage) {
        const currentWP = this.workPermitsInProject.find(item => item.self.id === this.$route.query.wpId)
        result = lodash.get(currentWP, 'hoistings', [])
      }
      result.forEach(hoisting => {
        if (this.hoistingsExpandMap[hoisting.id] === undefined) {
          this.hoistingsExpandMap[hoisting.id] = false
        }
        if (!this.hoistingsExpandMap[hoisting.id]) {
          if (hoisting.id === this.$route.query.hoistingId) {
            this.hoistingsExpandMap[hoisting.id] = true
          }
        }
      })
      return orderByPermitCode(result)
    },
    showHoistingModule () {
      return !!lodash.get(this.backEndRegionalEnv, 'moduleSwitch.hoistingModule')
    },
    showAddHoistingBtn () {
      if (!this.isProjectAvailable) return false
      if (isWorkPermitEditable(this.currentWorkPermit)) return false

      return this.isStandaloneContractor ||
        this.canContractorAddHoisting ||
        this.canVendorEditHoisting ||
        this.isStandaloneTenant ||
        this.isStandaloneTenancyContractor
    },
    hoistingLabel () {
      return getRegionalWorkPermitLabel('hoisting')
    },
    addHoistingLabel () {
      return getAddHoistingLabel()
    },
    hoistingDeleteConfirmHint () {
      return getHoistingConfirmHint(this.$t('action.delete'))
    },
    hoistingNoShowHint () {
      return getHoistingNoShowHint()
    },
    canContractorAddHoisting () {
      return allowCreate() && (this.myContractor || {}).companyId === this.contractorId && !disableTenantContractorEdit()
    },
    canVendorEditHoisting () {
      return this.isCurrentVendorAccount && this.contractorId === this.currentUser.externalId
    },
    canFollowUpHoisting () {
      return this.canProcessCurrentWP(tmsPermsTypes.workpermit.followUpHoisting)
    },
    contractorId () {
      return lodash.get(this.currentWorkPermit, 'contractor.id')
    },
    floorsOptions () {
      return this.currentWorkPermit.floors || []
    },
  },
  watch: {
    focusPermitId (val) {
      this.handleWpExpand()
      const tabIndex = this.$route.query.tabIndex
      if (tabIndex !== 0 && !tabIndex) {
        EventBus.emit('common-tabs-container-select-tab', '', tabIndex)
      }
      this.scrollToFocus(val)
    },
  },
  async mounted () {
    this.handleWpExpand()
    this.maxWAPPRHoistingNum = await getMaxWAPPRHoistingNum()
    this.scrollToFocus(this.focusPermitId)
  },
  methods: {
    ...mapActions('workPermit', [
      'createWorkPermit',
      'changeWorkPermitStatus',
      'deleteWorkPermit',
      'deleteHoistingLocalAndServer',
      'getWorkPermitById',
      'getWorkPermitsInProjectByProjectId',
    ]),
    ...mapMutations('workPermit', {
      addHoisting: mutationTypes.WORKPERMIT_ADD_NEW_HOISTING,
      updateHoistingValue: mutationTypes.WORKPERMIT_SET_HOISTING_ATTRIBUTE,
    }),
    canProcessCurrentWP (permType) {
      return this.isProjectAvailable && canProcessCurrentWP(permType)
    },
    showDotOption (data) {
      if (!this.isProjectAvailable) return false
      const code = lodash.get(data, 'code', '')
      return data.status === WORK_PERMIT_STATUS.DRAFT && !code
    },
    canHoistingDelete (data) {
      return data.status === WORK_PERMIT_STATUS.DRAFT || data.status === WORK_PERMIT_STATUS.REJECTED
    },
    showDotOptionForHoisting (data) {
      if (!this.isProjectAvailable) return false

      return (this.canHoistingDelete(data) && this.showAddHoistingBtn) || (data.status === WORK_PERMIT_STATUS.APPR && this.canFollowUpHoisting)
    },
    dotOptionForHoisting (data) {
      return this.canHoistingDelete(data) ? this.dotOption : this.dotOptionForNoShow
    },
    async _deleteWorkPermit () {
      if (this.chooseDeleteHoisting.type === WORK_PERMIT_TYPES.HOISTING) {
        await this.deleteHoistingLocalAndServer(this.chooseDeleteHoisting)
      }
    },
    async _updateHoistingToExpired () {
      if (this.chooseHoistingToExpired) {
        const result = await this.changeWorkPermitStatus({status: WORK_PERMIT_STATUS.EXPIRED,
          id: this.chooseHoistingToExpired.id,
          updatedAt: this.chooseHoistingToExpired.updatedAt,
          refresh: false })
        if (this.standalone) {
          await this.getWorkPermitById(this.detailData.id)
        } else {
          await this.getWorkPermitsInProjectByProjectId({
            projectId: result.projectId,
            changeCurrentWorkPermitId: this.detailData.id,
          })
        }
      }
    },
    async _addHoisting () {
      const payload = composeNewHoistingPayload(this.currentWorkPermit)
      const hoisting = await this.createWorkPermit(payload)
      this.addHoisting({ id: hoisting.id, contractorId: hoisting.contractorId })

      this.updateExpandMap(hoisting.id, true)
      if (!hoisting.standalone && hoisting.projectId) {
        await this.getWorkPermitsInProjectByProjectId({ projectId: hoisting.projectId })
      }

      EventBus.$emit('edit_when_add_hoisting', hoisting.id)
      setTimeout(() => {
        this.scrollTo(hoisting.id)
      }, 10)
    },
    scrollTo (id) {
      const element = document.getElementById(id)
      if (!element) return
      window.scrollTo(0, element.offsetTop + 10)
      EventBus.emit('window_scroll')
    },
    scrollToFocus (id) {
      if (!this.isStandalonePage || !id) return
      this.$nextTick(() => {
        this.scrollTo(id)
      })
    },
    openNoShowModal (hp) {
      this.chooseHoistingToExpired = hp
      this.$refs.noShowHoistingModal.open(this._updateHoistingToExpired)
    },
    openDeleteHoistingModal (hp) {
      this.chooseDeleteHoisting = hp
      this.$refs.deleteHoistingModal.open(this._deleteWorkPermit)
    },
    updateExpandMap (id, status) {
      this.$set(this.hoistingsExpandMap, id, status)
    },
    handleWpExpand () {
      let wpId = this.isStandalonePage ? this.$route.params.id : this.$route.query.wpId
      const wpExpandStatus = this.hoistingsExpandMap[wpId]
      if (!wpExpandStatus) {
        this.$set(this.hoistingsExpandMap, wpId, !this.$route.query.hoistingId)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/common';
.workpermit-detail {
  width: 100%;
  &__collapse {
    margin-bottom: 20px;
    & ::v-deep {
      .title-container {
        height: 71px;
      }
    }
  }
  &__content {
    padding: 20px
  }
}
.addNewHoisting {
  width: 230px;
  border-radius: 20px;
  color: white;
  background: #6e7a91;
  height: 40px;
  line-height: 40px;
  font-weight: bold;
  text-align: center;
  margin: 20px auto;
  cursor: pointer;
  margin-bottom: 0;
}

.workPermit_hint {
  margin-top: 10px;
  text-align: center;
  color:#E49405;
}
</style>
